import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Info, Warning } from "@phosphor-icons/react"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { formatDate, HYPHEN_CHARACTER } from "@/lib/format-date"
import { selectContentForLanguage } from "@/lib/select-content-for-language"
import { NewsGrid } from "@/modules/admin/news/components/news-grid"
import { PlannedNews } from "@/modules/admin/news/components/planned-news-"
import { ShowingNews } from "@/modules/admin/news/components/showing-news"
import { NewsType, PLANNING_NEWS } from "@/modules/admin/news/constants"
import {
  CriticalityLevel,
  NewsFragment$data,
  NewsFragment$key,
} from "@/modules/admin/news/services/__generated__/NewsFragment.graphql"
import { NewsFragment } from "@/modules/admin/news/services/NewsFragment"

interface NewsItemProps {
  news: NewsFragment$key
  onOpenEditNew: (data: NewsFragment$data) => void
  onShow: (data: NewsFragment$data) => void
  onDelete: (id: string) => void
  onStop: (id: string) => void
  type: NewsType
}

const NewsItem = ({ news, onOpenEditNew, onShow, onDelete, onStop, type }: NewsItemProps) => {
  const data = useFragment(NewsFragment, news)

  const { t } = useTranslation()
  const currentLanguage = useCurrentLanguage()
  const itemAppearance = getItemAppearance(data.criticalityLevel)

  return (
    <NewsGrid
      className={`grid h-12 cursor-pointer items-center gap-4 rounded-lg px-4 text-sm font-normal ${itemAppearance.textColor} ${itemAppearance.backgroundColor}`}
      onClick={() => onOpenEditNew(data)}>
      <span>{itemAppearance.icon}</span>
      <span className="truncate">
        {selectContentForLanguage(currentLanguage, {
          en: data.englishContent,
          de: data.germanContent,
          fr: data.frenchContent,
          it: data.italianContent,
        })}
      </span>
      <span>
        {data.endTime
          ? `${formatNewsTime(data.startTime)} ${HYPHEN_CHARACTER} ${formatNewsTime(data.endTime)}`
          : formatNewsTime(data.startTime)}
      </span>
      <span className="font-semibold uppercase">
        {data.displayFor.map((field) => t(`common.roles.${field}`)).join(" & ")}
      </span>
      {type === PLANNING_NEWS ? (
        <PlannedNews dataNews={data} onShowNews={() => onShow(data)} onDeleteNews={() => onDelete(data.id)} />
      ) : (
        <ShowingNews dataNews={data} onStopNews={() => onStop(data.id)} />
      )}
    </NewsGrid>
  )
}

const getItemAppearance = (level: CriticalityLevel) => {
  switch (level) {
    case "CRITICAL":
      return {
        backgroundColor: "bg-critical-surface",
        icon: <Warning weight="fill" className="text-on-critical-surface" />,
        textColor: "text-on-critical-surface",
      }
    case "WARNING":
      return {
        backgroundColor: "bg-warning-surface-subdued",
        icon: <Warning className="text-on-warning-surface" />,
        textColor: "text-on-warning-surface",
      }
    case "INFO":
      return { backgroundColor: "bg-surface-subdued", icon: <Info />, textColor: "text-on-surface" }
  }
}

const formatNewsTime = (newsTime: string | null) => {
  if (!newsTime) {
    return ""
  }
  const newsTimeObj = new Date(newsTime)
  return formatDate(newsTimeObj, "dd.MM.yyyy HH:mm")
}

export { NewsItem }
